import { useMemo, useRef, useState } from 'react';
import { AddGuestToFunctions } from '../../api/api';
import { isPastFunction, nameInput, tenDigits, toggleWithWedding, newGuestFunctionIds, openAutoFunctionIds, guestTypeForGroup } from '../../utils/helpers';
import { parseFamilyNames } from '../../utils/quickEntry';

// "Quick add" — a hundred guests in one sitting.
//
// Built around what the event's OWN data says: 83% of guests are in a
// household, 95 of 100 family members simply repeat the head's surname, only
// half have a phone number of their own, and five combinations of functions
// cover nine guests in ten. Email, relation and address are never filled at
// all. So:
//   • ONE LINE IS ONE HOUSEHOLD - surname, the head's name, an optional
//     number, then "others in the family" as plain names ("Kamla, Ravi 98220…").
//     The line saves as a family in a single call, not four lines that have to
//     be joined up afterwards.
//   • Group and Guest-is are set ONCE for the batch, not typed per guest.
//   • The functions of a line are copied by the next line, and the sets this
//     event actually uses are one click away (quickPresets).
//   • Surnames already in the event are offered as you type.
// Saved lines leave the grid; lines with a problem stay, marked, with the
// reason. Used by useMembers, which hands in what it already knows.
const START_ROWS = 5;
const lc = (v) => String(v || '').trim().toLowerCase();

export function useQuickAdd({
  event, organiser, user, functions, rows, isMarriage, lockedSide, defaultSide, sideMissing, newOwner,
  fnFilter, refresh, toast, readDefaults, writeDefaults, rowFunctionIds,
}) {
  const me = user.UserId;
  const keySeq = useRef(0);
  const [quickOpen, setQuickOpen] = useState(false);
  // Like every other way of adding, a quick list starts PRIVATE; one toggle
  // makes the whole batch visible to the other members.
  const [quickVisibility, setQuickVisibility] = useState('Private');
  // Set once, used by every line of the batch (54% of guests carry a group;
  // nobody types it per person).
  // The group is the ONLY thing asked - "Own family" and "Business" carry
  // their rules with them, every other group is a plain circle of people.
  const [quickGroup, setQuickGroup] = useState('');
  const [quickRows, setQuickRows] = useState([]);
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickProgress, setQuickProgress] = useState({ done: 0, total: 0 });

  // Only functions that are not over can be invited to.
  const quickFunctions = useMemo(() => functions.filter(f => !isPastFunction(f)), [functions]);

  // The sets of functions this event ACTUALLY uses, commonest first - five of
  // them cover nine guests in ten, so they are one click instead of six.
  const quickPresets = useMemo(() => {
    const open = new Map(quickFunctions.map(f => [String(f.FunctionId), f.FunctionName]));
    const seen = new Map();
    const perGuest = new Map();
    rows.forEach(g => {
      const id = String(g.GuestId);
      if (!perGuest.has(id)) perGuest.set(id, new Set());
      rowFunctionIds(g).forEach(f => { if (open.has(String(f))) perGuest.get(id).add(String(f)); });
    });
    perGuest.forEach(set => {
      const ids = [...set].sort((a, b) => Number(a) - Number(b));
      if (!ids.length) return;
      const key = ids.join(',');
      if (!seen.has(key)) seen.set(key, { key, ids, count: 0, label: ids.map(i => open.get(i)).join(' + ') });
      seen.get(key).count += 1;
    });
    const list = [...seen.values()].sort((a, b) => b.count - a.count).slice(0, 5);
    const allIds = [...open.keys()];
    if (allIds.length > 1 && !list.some(p => p.ids.length === allIds.length)) {
      list.push({ key: 'all', ids: allIds, count: 0, label: 'All functions' });
    }
    return list;
  }, [rows, quickFunctions, rowFunctionIds]);

  // Surnames already in this event, for the type-ahead: Ailani, Dhankani,
  // Punjabi … - far more use than a contact list when a whole clan is added.
  const quickSurnames = useMemo(() => {
    const m = new Map();
    rows.forEach(g => { const v = String(g.Surname || '').trim(); if (v) m.set(lc(v), v); });
    return [...m.values()].sort((a, b) => a.localeCompare(b));
  }, [rows]);
  // Groups already used, so the batch picks one instead of typing it.
  const quickGroups = useMemo(() => {
    const m = new Map();
    rows.forEach(g => { const v = String(g.GroupName || '').trim(); if (v) m.set(lc(v), v); });
    return [...m.values()].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const newRow = (FunctionIds = [], weddingOff = false) => {
    keySeq.current += 1;
    return { key: keySeq.current, Surname: '', GuestName: '', MobileNumber: '', Family: '', FunctionIds, weddingOff, error: '' };
  };
  const isFilled = (r) => !!(String(r.GuestName).trim() || String(r.Surname).trim() || String(r.MobileNumber).trim());

  // "Kamla, Ravi 9822011111; Priya" → the other people on this card. A piece
  // is a name, with a 10-digit number after it when that person has one; the
  // surname comes from the head, which is what 95 of 100 members do anyway.
  const familyMembers = (r) => parseFamilyNames(r.Family, r.Surname);

  // Where a fresh grid starts: the filtered function (plus the Wedding), else
  // the functions used last time, else nothing.
  const startingFunctions = () => {
    const open = new Set(quickFunctions.map(f => String(f.FunctionId)));
    const preset = quickFunctions.find(f => String(f.FunctionId) === String(fnFilter));
    if (preset) return newGuestFunctionIds([String(preset.FunctionId)], quickFunctions);
    // The set used last time is repeated EXACTLY - "Thread Ceremony +
    // Reception" stays those two, instead of the auto rule adding the Wedding
    // back to a set somebody has just chosen deliberately.
    const remembered = (readDefaults(event.EventId).FunctionIds || []).map(String).filter(id => open.has(id));
    return remembered.length ? remembered : newGuestFunctionIds([], quickFunctions);
  };

  const openQuick = () => {
    setQuickVisibility('Private');
    setQuickGroup('');
    if (sideMissing) { toast('Ask your event admin to set your side (Groom or Bride) before adding guests'); return; }
    if (!quickFunctions.length) { toast('No open function to invite guests to'); return; }
    const fns = startingFunctions();
    const autoOff = openAutoFunctionIds(quickFunctions).some(id => !fns.includes(id));
    setQuickRows(Array.from({ length: START_ROWS }, () => newRow(fns, autoOff)));
    setQuickProgress({ done: 0, total: 0 });
    setQuickOpen(true);
  };
  const closeQuick = () => { if (!quickSaving) setQuickOpen(false); };

  const setQuickField = (key, field, value) => setQuickRows(list => list.map(r => {
    if (r.key !== key) return r;
    const v = field === 'MobileNumber' ? tenDigits(value) : nameInput(value);
    return { ...r, [field]: v, error: '' };
  }));

  const toggleQuickFunction = (key, functionId) => setQuickRows(list => list.map(r => {
    if (r.key !== key) return r;
    const res = toggleWithWedding(r.FunctionIds, functionId, quickFunctions, r.weddingOff);
    return { ...r, FunctionIds: res.ids, weddingOff: res.weddingOff, error: '' };
  }));

  // Column header checkbox: tick this function for every line, or clear it
  // from every line when all of them have it already.
  const columnState = (functionId) => {
    const id = String(functionId);
    const n = quickRows.filter(r => r.FunctionIds.includes(id)).length;
    return n === 0 ? 'none' : n === quickRows.length ? 'all' : 'some';
  };
  const toggleQuickColumn = (functionId) => {
    const id = String(functionId);
    const allOn = columnState(id) === 'all';
    setQuickRows(list => list.map(r => {
      const has = r.FunctionIds.includes(id);
      if (allOn === has) {
        const res = toggleWithWedding(r.FunctionIds, id, quickFunctions, r.weddingOff);
        return { ...r, FunctionIds: res.ids, weddingOff: res.weddingOff };
      }
      return r;
    }));
  };

  // A preset replaces the line's functions outright (the Wedding rule still
  // applies to what it leaves ticked).
  const applyPreset = (key, ids) => setQuickRows(list => list.map(r => (
    r.key === key ? { ...r, FunctionIds: [...ids], weddingOff: false, error: '' } : r
  )));
  // …and for every line at once, which is how a batch usually starts.
  const applyPresetToAll = (ids) => setQuickRows(list => list.map(r => (
    { ...r, FunctionIds: [...ids], weddingOff: false, error: '' }
  )));

  // New lines copy the functions of the last line.
  const addQuickRows = (n = 1) => setQuickRows(list => {
    const last = list[list.length - 1];
    const fns = last ? last.FunctionIds : startingFunctions();
    return [...list, ...Array.from({ length: n }, () => newRow(fns, last?.weddingOff || false))];
  });
  const removeQuickRow = (key) => setQuickRows(list => (list.length > 1 ? list.filter(r => r.key !== key) : list));

  const filledCount = quickRows.filter(isFilled).length;

  // Why this line cannot be saved ('' = fine). `earlier` = lines above it.
  const problemFor = (r, earlier) => {
    const name = String(r.GuestName).trim();
    const mobile = String(r.MobileNumber).trim();
    if (!name) return 'Name is required';
    if (mobile && !/^\d{10}$/.test(mobile)) return 'Mobile must be 10 digits';
    if (!r.FunctionIds.length) return 'Tick at least one function';
    const person = `${lc(r.Surname)}|${lc(name)}`;
    const twin = earlier.find(o => (mobile && o.MobileNumber === mobile) || `${lc(o.Surname)}|${lc(o.GuestName)}` === person);
    if (twin) return 'Listed twice in this grid';
    // Already invited to one of these functions (same number, or same name)?
    const clash = rows.find(g => rowFunctionIds(g).some(id => r.FunctionIds.includes(String(id)))
      && ((mobile && String(g.MobileNumber || '') === mobile) || `${lc(g.Surname)}|${lc(g.GuestName)}` === person));
    if (clash) {
      const by = String(clash.CreatedBy) === String(me) ? 'you' : ((clash.AddedByName || '').trim() || 'another member');
      return `Already invited to ${clash.FunctionName || 'one of these functions'} (added by ${by})`;
    }
    return '';
  };

  const saveQuick = async () => {
    const filled = quickRows.filter(isFilled);
    if (!filled.length) { toast('Type at least one guest'); return; }
    // Check every line first; bad lines stay in the grid with their reason.
    const checked = filled.map((r, i) => ({ r, error: problemFor(r, filled.slice(0, i)) }));
    const good = checked.filter(c => !c.error).map(c => c.r);
    const errors = new Map(checked.filter(c => c.error).map(c => [c.r.key, c.error]));

    setQuickSaving(true);
    setQuickProgress({ done: 0, total: good.length });
    const saved = new Set();
    // Saved under the person whose list is open (useMembers newOwner), else me.
    const owner = newOwner || { id: me, name: `${user.Surname || ''} ${user.Name || ''}`.trim() || user.Name || null, mobile: user.MobileNumber || null };
    const addedByName = owner.name;
    const side = isMarriage ? (lockedSide || defaultSide) : 'Common';
    for (const r of good) {
      try {
        // One line is one household: the head plus whoever was typed into
        // "others in the family", saved in a single call so they share a card.
        const members = familyMembers(r);
        await AddGuestToFunctions({
          EventId: event.EventId, OrganiserId: organiser.OrganiserId, FunctionIds: r.FunctionIds,
          Surname: String(r.Surname).trim() || null, GuestName: String(r.GuestName).trim(),
          MobileNumber: r.MobileNumber || null, Side: side, Visibility: quickVisibility, ExpectedCount: 1,
          GroupName: quickGroup || null, GuestType: guestTypeForGroup(quickGroup),
          InviteStatus: 'Pending',
          IsFamilyHead: members.length ? 1 : 0, FamilyMembers: members,
          AddedByName: addedByName, AddedByMobile: owner.mobile,
          Valid: 1, CreatedBy: owner.id, UpdatedBy: me,
        });
        saved.add(r.key);
      } catch {
        errors.set(r.key, 'Save failed - check your connection and save again');
      }
      setQuickProgress(p => ({ ...p, done: p.done + 1 }));
    }
    setQuickSaving(false);

    const lastFns = good.length ? good[good.length - 1].FunctionIds : null;
    if (lastFns) writeDefaults(event.EventId, { ...readDefaults(event.EventId), FunctionIds: lastFns });
    // Keep only what still needs attention (errors + untouched blank lines);
    // top the grid back up so the next batch can be typed straight away.
    setQuickRows(list => {
      const left = list.filter(r => !saved.has(r.key)).map(r => ({ ...r, error: errors.get(r.key) || '' }));
      const fns = lastFns || left[left.length - 1]?.FunctionIds || [];
      const blanks = Math.max(0, START_ROWS - left.length);
      return [...left, ...Array.from({ length: blanks }, () => newRow(fns))];
    });
    if (saved.size) refresh();
    const bad = errors.size;
    const people = good.filter(r => saved.has(r.key)).reduce((n, r) => n + 1 + familyMembers(r).length, 0);
    toast(`${people} guest${people === 1 ? '' : 's'} added in ${saved.size} household${saved.size === 1 ? '' : 's'}`
      + (bad ? ` · ${bad} line${bad === 1 ? '' : 's'} need a fix` : ''));
  };

  return {
    quickOpen, openQuick, closeQuick, quickRows, quickFunctions, quickSaving, quickProgress, filledCount,
    quickVisibility, setQuickVisibility, quickGroup, setQuickGroup,
    quickPresets, applyPreset, applyPresetToAll, quickSurnames, quickGroups, familyMembers,
    setQuickField, toggleQuickFunction, toggleQuickColumn, columnState, addQuickRows, removeQuickRow, saveQuick,
  };
}
